import { useMemo, useState } from "react";
import { BarChart3, PenLine, ChevronDown, ListChecks } from "lucide-react";

const FIELDS = [
  { key: "brandName", label: "Brand name" },
  { key: "brandSuffix", label: "Brand suffix" },
  { key: "headerTag", label: "Header right tag" },
  { key: "heroTitle", label: "Hero title" },
  { key: "footerSignoff", label: "Footer sign-off" },
  { key: "footerTeam", label: "Footer team name" },
];

export default function StepPreview({ buildHtml, includeCharts, setIncludeCharts, includeFindings, setIncludeFindings, ai, branding, setBranding, goTo }) {
  const [editOpen, setEditOpen] = useState(false);
  const html = useMemo(() => buildHtml(includeCharts), [buildHtml, includeCharts]);

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center">Exactly what they'll receive</h1>
      <p className="text-center text-gray-500 mt-3 mb-6 text-sm sm:text-base">
        This preview is the real email — what you see here is byte-for-byte what lands in the inbox.
      </p>

      <div className="flex justify-center gap-3 mb-6 flex-wrap">
        <button
          onClick={() => setIncludeCharts(!includeCharts)}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${includeCharts ? "bg-[#E6F4EA] border-[#137333] text-[#137333]" : "bg-white border-gray-300 text-gray-500"}`}
          data-testid="v1-charts-toggle"
        >
          <BarChart3 className="w-4 h-4" />
          {includeCharts ? "Charts included" : "Charts hidden"}
        </button>
        {ai.findings.trim() && (
          <button
            onClick={() => setIncludeFindings(!includeFindings)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${includeFindings ? "bg-[#E8F0FE] border-[#1A73E8] text-[#1A73E8]" : "bg-white border-gray-300 text-gray-500"}`}
            data-testid="v1-findings-toggle"
          >
            <ListChecks className="w-4 h-4" />
            {includeFindings ? "Key movements included" : "Key movements hidden"}
          </button>
        )}
        <button
          onClick={() => setEditOpen(!editOpen)}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${editOpen ? "bg-[#FEF7E0] border-[#B06000] text-[#B06000]" : "bg-white border-gray-300 text-gray-500"}`}
          data-testid="v1-edit-branding-toggle"
        >
          <PenLine className="w-4 h-4" />
          Edit header & footer
          <ChevronDown className={`w-4 h-4 transition-transform ${editOpen ? "rotate-180" : ""}`} />
        </button>
      </div>

      {editOpen && (
        <div className="v1-card p-5 mb-6 grid sm:grid-cols-3 gap-4 v1-fade-in" data-testid="v1-branding-panel">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">{f.label}</label>
              <input
                value={branding[f.key]}
                onChange={(e) => setBranding({ ...branding, [f.key]: e.target.value })}
                className="w-full mt-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-green-700"
                data-testid={`v1-branding-${f.key}`}
              />
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl overflow-hidden border border-[#e4e2da] shadow-sm" data-testid="v1-email-preview">
        <iframe
          title="Email preview"
          srcDoc={html}
          className="w-full bg-[#F1F3F4]"
          style={{ height: "75vh", border: "none" }}
          sandbox=""
        />
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={() => goTo(1)} className="v1-btn-ghost" data-testid="v1-preview-back-button">← Edit content</button>
        <button onClick={() => goTo(3)} className="v1-btn-primary" data-testid="v1-preview-continue-button">Looks good — send it →</button>
      </div>
    </div>
  );
}
